import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Types } from '../state/actionTypes'
import { getPersistedEmail, hasStoredTokens } from './crossTabSession'

const WATCHED_KEYS = ['tokens', 'persist:root']

/**
 * Mantiene coherente la sesión entre pestañas del mismo navegador.
 *
 * Los tokens y `persist:root` viven en `localStorage` (compartido), pero cada
 * pestaña decide con su Redux en memoria, que `redux-persist` solo rehidrata al
 * cargar. Sin esto, una pestaña que ya estaba en `/login` sigue mostrando el
 * formulario cuando otra inicia sesión: un segundo `sign_in` crea otro `client`
 * y deja huérfano el primero (tope de dispositivos por usuario), y editar el
 * email ahí borraba los tokens de la pestaña viva.
 *
 * `evaluate()` lee siempre `localStorage` (no el payload del evento) para que
 * sirva igual en `storage`, `focus` y `visibilitychange` — Chrome puede
 * congelar pestañas en segundo plano y perder eventos `storage`.
 *
 * | Esta pestaña | localStorage                                  | Acción        |
 * |--------------|-----------------------------------------------|---------------|
 * | sin email    | tokens vivos + persist:root con email         | recargar      |
 * | con email    | tokens vacíos                                 | CLEAN_AUTH    |
 * | con email    | tokens vivos + persist:root con otro email    | recargar      |
 *
 * El logout se decide SOLO por `tokens`: cada pestaña escribe su propio
 * `persist:root` completo, así que una pestaña vieja sin sesión puede dejar
 * `email: ''` ahí sin que nadie haya cerrado sesión.
 *
 * Sin bucles: `CLEAN_AUTH` solo se despacha si esta pestaña tiene email, y
 * re-escribir `tokens = '{}'` con el valor que ya tiene no emite `storage`.
 *
 * `CLEAN_AUTH` no llama a `sign_out` (los tokens ya no existen) ni a
 * `clearLocalDatabase()` (Dexie es compartido: la pestaña que cerró sesión ya
 * lo limpió o puso en cuarentena lo pendiente).
 */
function CrossTabSessionGuard({ email, dispatch, onReload = () => window.location.reload() }) {
  const emailRef = useRef(email)
  emailRef.current = email

  const dispatchRef = useRef(dispatch)
  dispatchRef.current = dispatch

  const onReloadRef = useRef(onReload)
  onReloadRef.current = onReload

  useEffect(() => {
    const evaluate = () => {
      const currentEmail = emailRef.current
      const tokensAlive = hasStoredTokens()
      const persistedEmail = getPersistedEmail()

      if (currentEmail) {
        if (!tokensAlive) {
          dispatchRef.current({ type: Types.CLEAN_AUTH })
          return
        }
        if (persistedEmail && persistedEmail !== currentEmail) onReloadRef.current()
        return
      }

      if (tokensAlive && persistedEmail) onReloadRef.current()
    }

    const onStorage = event => {
      if (event.key !== null && !WATCHED_KEYS.includes(event.key)) return
      evaluate()
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') evaluate()
    }

    window.addEventListener('storage', onStorage)
    window.addEventListener('focus', evaluate)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('focus', evaluate)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return null
}

const mapStateToProps = state => ({ email: state.profile.email })

export { CrossTabSessionGuard }
export default connect(mapStateToProps)(CrossTabSessionGuard)
